// Page search: Cmd/Ctrl+K anywhere, or the search button in the bar. Type a few letters, press Enter.
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MagnifyingGlass } from '@phosphor-icons/react';

function score(item, query) {
  if (!query) return 1;
  const label = item.label.toLowerCase();
  const haystack = `${label} ${item.group} ${item.description} ${item.path}`.toLowerCase();
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.every((word) => haystack.includes(word))) return 0;
  if (label.startsWith(query.toLowerCase())) return 3;
  if (words.every((word) => label.includes(word))) return 2;
  return 1;
}

export default function CommandMenu({ open, onClose, items }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const input = useRef(null);
  const list = useRef(null);

  const results = useMemo(() => items
    .map((item, index) => ({ item, index, rank: score(item, query.trim()) }))
    .filter((entry) => entry.rank > 0)
    .sort((a, b) => b.rank - a.rank || a.index - b.index)
    .map((entry) => entry.item), [items, query]);

  useEffect(() => {
    if (!open) return undefined;
    setQuery('');
    setCursor(0);
    const previous = document.activeElement;
    const timer = window.setTimeout(() => input.current?.focus(), 0);
    const root = document.documentElement;
    const overflow = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(timer);
      root.style.overflow = overflow;
      if (previous && typeof previous.focus === 'function') previous.focus({ preventScroll: true });
    };
  }, [open]);

  useEffect(() => { setCursor(0); }, [query]);

  useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  if (!open) return null;

  const go = (item) => {
    if (!item) return;
    onClose();
    navigate(item.path);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') { event.preventDefault(); setCursor((value) => Math.min(results.length - 1, value + 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setCursor((value) => Math.max(0, value - 1)); }
    else if (event.key === 'Enter') { event.preventDefault(); go(results[cursor]); }
    else if (event.key === 'Escape') { event.preventDefault(); onClose(); }
  };

  return createPortal(
    <div className="rc-cmd-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className="rc-cmd" role="dialog" aria-modal="true" aria-label="Search pages">
        <div className="rc-cmd__field">
          <MagnifyingGlass size={20} aria-hidden="true" />
          <input
            ref={input}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search pages"
            aria-label="Search pages"
            aria-controls="rc-cmd-results"
            aria-activedescendant={results[cursor] ? `rc-cmd-${results[cursor].path}` : undefined}
            autoComplete="off"
            spellCheck="false"
            enterKeyHint="go"
          />
          <kbd className="rc-kbd">Esc</kbd>
        </div>
        <ul className="rc-cmd__results" id="rc-cmd-results" role="listbox" ref={list}>
          {results.map((item, index) => {
            const Icon = item.icon;
            return (
              <li
                key={item.path}
                id={`rc-cmd-${item.path}`}
                role="option"
                aria-selected={index === cursor}
                className="rc-cmd__item"
                onMouseMove={() => setCursor(index)}
                onClick={() => go(item)}
              >
                <span className="rc-nav-tile">{Icon ? <Icon size={18} aria-hidden="true" /> : null}</span>
                <span className="rc-cmd__text">
                  <span className="rc-cmd__label">{item.label}</span>
                  <span className="rc-cmd__meta">{item.group}</span>
                </span>
                <ArrowRight size={16} aria-hidden="true" className="rc-cmd__go" />
              </li>
            );
          })}
          {!results.length ? <li className="rc-cmd__none">No page matches “{query.trim()}”. Try a shorter word.</li> : null}
        </ul>
      </div>
    </div>,
    document.body,
  );
}
